import { getConversationPreview } from '@/lib/conversation-selectors';
import { Span, Text } from '@chakra-ui/react';
import type {
  Conversation,
  ConversationGiftMarkerData,
  DashboardConversationLatestSystemEvent,
} from '@fluenceur/types';
import { CreatorHubSystemEventName, isCreatorParticipant, MessageTypeEnum } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { isGiftMarker } from './system-event-presentation';
import { useSystemEventLabel } from './use-system-event-label';

interface LatestMessageProps {
  conversation: Conversation;
  isNew: boolean;
}

interface MentionMessageProps {
  creatorPlatformUsername: string;
}

interface BaseMessageProps {
  children: React.ReactNode;
  color?: string;
  fontWeight?: string;
  fontStyle?: string;
  fontSize?: string;
}

const BaseMessage = ({
  children,
  color = 'navDark.textMuted',
  fontWeight = 'light',
  fontStyle = 'normal',
  fontSize = '12px',
}: BaseMessageProps) => (
  <Text
    fontSize={fontSize}
    color={color}
    fontWeight={fontWeight}
    fontStyle={fontStyle}
    overflow="hidden"
    textOverflow="ellipsis"
    whiteSpace="nowrap"
  >
    {children}
  </Text>
);

const MentionMessage = ({ creatorPlatformUsername }: MentionMessageProps) => (
  <BaseMessage color="navDark.textMuted" fontSize="xs">
    <Span as="span">@{creatorPlatformUsername}</Span>
    {` mentioned you`}
  </BaseMessage>
);

const TextMessage = ({ content, isNew }: { content: string; isNew: boolean }) => (
  <BaseMessage color={isNew ? 'navDark.textStrong' : 'navDark.textMuted'}>{content}</BaseMessage>
);

const SystemMessage = ({ content }: { content: string }) => (
  <BaseMessage color="navDark.textSubtle" fontStyle="italic">
    {content}
  </BaseMessage>
);

/**
 * A thread whose newest item is a marker, not a message. The Inbox names the
 * fact in one clause — a thread of pure watching is not an empty thread.
 */
const SystemEventPreview = ({ event }: { event: DashboardConversationLatestSystemEvent }) => {
  const {
    collabMarkerTitle,
    giftMarkerTitle,
    systemEventTitle,
    mentionTitle,
    receptionQuestionTitle,
    receptionCountedTitle,
  } = useSystemEventLabel();

  const label = (() => {
    switch (event.event) {
      case CreatorHubSystemEventName.MENTION_RECEIVED:
        return mentionTitle(event.data.mentionType);
      case CreatorHubSystemEventName.COLLAB_BRIEF_ACCEPTED:
      case CreatorHubSystemEventName.COLLAB_PAYMENT_DUE:
      case CreatorHubSystemEventName.COLLAB_PROPOSED:
        return collabMarkerTitle(event.event, event.data.collabName);
      case CreatorHubSystemEventName.PRODUCT_RECEPTION_QUESTION:
        return receptionQuestionTitle(event.data.productName);
      case CreatorHubSystemEventName.PRODUCT_RECEPTION_COUNTED:
        return receptionCountedTitle(event.data.receivedAt, event.data.productName);
      default:
        return isGiftMarker(event.event)
          ? giftMarkerTitle(event.event, (event.data as ConversationGiftMarkerData).productTitle)
          : systemEventTitle(event.event);
    }
  })();

  return <SystemMessage content={label} />;
};

const LatestMessage = ({ conversation, isNew }: LatestMessageProps) => {
  const { t } = useTranslation();
  const preview = getConversationPreview(conversation);

  if (preview.event) return <SystemEventPreview event={preview.event} />;

  const content = conversation.latestMessage?.content || t('messages.thread.start');
  const messageType = conversation.latestMessage?.type as MessageTypeEnum;

  const participant = conversation.participant;
  const isCreator = isCreatorParticipant(participant);
  const platformUsername = isCreator ? participant.socialProfile?.platformUsername : null;

  switch (messageType) {
    case MessageTypeEnum.STORY_MENTION:
    case MessageTypeEnum.CAPTION_MENTION:
      return <MentionMessage creatorPlatformUsername={platformUsername || 'unknown'} />;
    case MessageTypeEnum.INVITATION:
      return <SystemMessage content={content} />;
    case MessageTypeEnum.TEXT:
    default:
      return <TextMessage content={content} isNew={isNew} />;
  }
};

export default LatestMessage;
